import {
  reviewListResponseSchema,
  type ReviewListResponse,
  type ReviewFilters,
  type Review,
} from '../types';
import {
  authFetch, API_BASE_URL 
} from '@/lib/api-client';

const mockReviews: ReviewListResponse = {
  data: [
    {
      reviewId: 'rev-001',
      employeeId: 'emp-101',
      employeeName: 'Maria Garcia',
      employeeRole: 'Line Cook',
      reviewerId: 'user-001',
      reviewerName: 'Chef Martinez',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      period: 'quarterly',
      periodStart: '2025-10-01',
      periodEnd: '2025-12-31',
      status: 'completed',
      overallRating: 4,
      strengths: 'Excellent attention to detail in plating. Consistently delivers high-quality dishes. Great team player who helps train new staff.',
      areasForImprovement: 'Could improve speed during peak service hours. Should work on communication with front-of-house staff.',
      achievements: 'Received 15 guest compliments this quarter. Zero food safety incidents. Successfully trained 2 new line cooks.',
      developmentPlan: 'Enroll in advanced culinary certification program. Shadow sous chef during busy services to improve speed.',
      employeeComments: 'I appreciate the feedback and am committed to improving my speed while maintaining quality.',
      createdAt: '2026-01-05T09:00:00Z',
      completedAt: '2026-01-10T14:30:00Z',
      dueDate: '2026-01-15',
    },
    {
      reviewId: 'rev-002',
      employeeId: 'emp-102',
      employeeName: 'James Wilson',
      employeeRole: 'Storekeeper',
      reviewerId: 'user-002',
      reviewerName: 'Sarah Chen',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      period: 'quarterly',
      periodStart: '2025-10-01',
      periodEnd: '2025-12-31',
      status: 'pending-approval',
      overallRating: 5,
      strengths: 'Outstanding inventory management skills. Proactive in identifying and resolving stock issues. Excellent organization.',
      areasForImprovement: 'Could improve documentation of inventory procedures for backup coverage.',
      achievements: 'Achieved zero stock-outs for 30 consecutive days. Reduced inventory variance from 4% to 2.5%.',
      developmentPlan: 'Create comprehensive inventory procedure documentation. Cross-train receiving clerk on storeroom operations.',
      createdAt: '2026-01-08T10:00:00Z',
      completedAt: null,
      dueDate: '2026-01-20',
    },
    {
      reviewId: 'rev-003',
      employeeId: 'emp-103',
      employeeName: 'David Kim',
      employeeRole: 'Receiving Clerk',
      reviewerId: 'user-001',
      reviewerName: 'Chef Martinez',
      siteId: 'site-002',
      siteName: 'Deck 9 Buffet',
      period: 'quarterly',
      periodStart: '2025-10-01',
      periodEnd: '2025-12-31',
      status: 'in-progress',
      createdAt: '2026-01-12T08:00:00Z',
      completedAt: null,
      dueDate: '2026-01-25',
    },
    {
      reviewId: 'rev-004',
      employeeId: 'emp-104',
      employeeName: 'Emily Brown',
      employeeRole: 'Sous Chef',
      reviewerId: 'user-001',
      reviewerName: 'Chef Martinez',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      period: 'annual',
      periodStart: '2025-01-01',
      periodEnd: '2025-12-31',
      status: 'draft',
      createdAt: '2026-01-15T09:00:00Z',
      completedAt: null,
      dueDate: '2026-02-01',
    },
    {
      reviewId: 'rev-005',
      employeeId: 'emp-105',
      employeeName: 'Michael Torres',
      employeeRole: 'Prep Cook',
      reviewerId: 'user-003',
      reviewerName: 'Mike Johnson',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      period: 'quarterly',
      periodStart: '2025-10-01',
      periodEnd: '2025-12-31',
      status: 'completed',
      overallRating: 3,
      strengths: 'Reliable and punctual. Good knife skills. Follows recipes accurately.',
      areasForImprovement: 'Needs to improve time management. Should take more initiative in prep planning.',
      achievements: 'Completed food safety certification. Improved prep speed by 15%.',
      developmentPlan: 'Work with sous chef on daily prep planning. Set daily prep completion targets.',
      employeeComments: 'I will focus on better time management and being more proactive.',
      createdAt: '2026-01-03T11:00:00Z',
      completedAt: '2026-01-08T16:00:00Z',
      dueDate: '2026-01-10',
    },
  ],
  pagination: {
    nextToken: null,
    totalCount: 5,
  },
};

function buildReviewsQueryParams(filters?: ReviewFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters?.employeeId) params.set('employeeId', filters.employeeId);
  if (filters?.reviewerId) params.set('reviewerId', filters.reviewerId);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.period) params.set('period', filters.period);
  if (filters?.siteId) params.set('siteId', filters.siteId);
  if (filters?.search) params.set('search', filters.search);
  return params;
}

async function fetchReviewsFromApi(filters?: ReviewFilters): Promise<ReviewListResponse> {
  const params = buildReviewsQueryParams(filters);
  const queryString = params.toString();
  const url = queryString
    ? `${API_BASE_URL}/v1/performance/reviews?${queryString}`
    : `${API_BASE_URL}/v1/performance/reviews`;

  const response = await authFetch(url);

  if (!response.ok) {
    throw new ReviewsApiError(response.status, `Failed to fetch reviews: ${response.statusText}`);
  }

  const data: unknown = await response.json();
  return reviewListResponseSchema.parse(data);
}

export async function getReviewsList(filters?: ReviewFilters): Promise<ReviewListResponse> {
  if (API_BASE_URL) {
    return fetchReviewsFromApi(filters);
  }

  await new Promise((resolve) => setTimeout(resolve, 500));
  
  const filtered = filterReviewsData(mockReviews.data, filters);

  return {
    data: filtered,
    pagination: {
      nextToken: null,
      totalCount: filtered.length,
    },
  };
}

function matchesReviewSearch(review: Review, search: string): boolean {
  const searchLower = search.toLowerCase();
  return (
    review.employeeName.toLowerCase().includes(searchLower) ||
    review.reviewerName.toLowerCase().includes(searchLower)
  );
}

function filterReviewsData(data: Review[], filters?: ReviewFilters): Review[] {
  if (!filters) return data;
  
  return data.filter((r) => {
    if (filters.status && r.status !== filters.status) return false;
    if (filters.period && r.period !== filters.period) return false;
    if (filters.search && !matchesReviewSearch(r, filters.search)) return false;
    return true;
  });
}

export class ReviewsApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string
  ) {
    super(message);
    this.name = 'ReviewsApiError';
  }
}
